import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { PageTitle, Card, F, Toggle, SingleImage } from "./AdminUI";

export default function PostsAdmin() {
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({ queryKey: ["admin-posts"], queryFn: () => api.get("/admin/posts").then((r) => r.data) });
  const del = async (p) => {
    if (!window.confirm(`Eliminare "${p.title}"?`)) return;
    await api.delete(`/admin/posts/${p.id}`);
    toast.success("Articolo eliminato");
    qc.invalidateQueries({ queryKey: ["admin-posts"] }); qc.invalidateQueries({ queryKey: ["posts"] });
  };
  return (
    <div data-testid="admin-posts">
      <PageTitle title="Diario di bordo" subtitle="Articoli, riflessioni ed estratti del libro" action={<Link to="/admin/diario/nuovo" data-testid="admin-post-new" className="btn-primary !py-2.5 !px-5"><Plus size={14} /> Nuovo articolo</Link>} />
      <Card className="!p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wider text-stone"><tr><th className="text-left p-4 font-medium">Titolo</th><th className="text-left p-4 font-medium hidden md:table-cell">Categoria</th><th className="text-left p-4 font-medium">Data</th><th className="text-left p-4 font-medium">Stato</th><th className="p-4" /></tr></thead>
          <tbody className="divide-y divide-gray-100">
            {items.map((p) => (
              <tr key={p.id} data-testid={`admin-post-row-${p.slug}`} className="hover:bg-gray-50">
                <td className="p-4 font-medium">{p.title}<div className="text-xs text-stone font-normal">/{p.slug}</div></td>
                <td className="p-4 hidden md:table-cell">{p.category}</td>
                <td className="p-4 text-xs text-stone">{new Date(p.published_at).toLocaleDateString("it-IT")}</td>
                <td className="p-4"><span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${p.published ? "bg-green-50 text-green-700" : "bg-gray-100 text-stone"}`}>{p.published ? "Pubblicato" : "Bozza"}</span></td>
                <td className="p-4 text-right whitespace-nowrap"><Link to={`/admin/diario/${p.id}`} className="inline-flex p-2 text-stone hover:text-ink"><Pencil size={15} /></Link><button onClick={() => del(p)} className="inline-flex p-2 text-stone hover:text-red-600"><Trash2 size={15} /></button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-6 text-sm text-stone">Nessun articolo. Crea il primo del Diario di bordo.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

const EMPTY = { title: "", slug: "", category: "Diario di bordo", excerpt: "", content: "", cover_image: "", published: true, published_at: new Date().toISOString() };

export function PostForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  useEffect(() => { if (id) api.get(`/admin/posts/${id}`).then((r) => setF({ ...EMPTY, ...r.data })); }, [id]);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...f }; delete payload.id; delete payload.created_at; delete payload.updated_at;
      if (id) await api.put(`/admin/posts/${id}`, payload); else await api.post("/admin/posts", payload);
      toast.success("Articolo salvato");
      qc.invalidateQueries({ queryKey: ["admin-posts"] }); qc.invalidateQueries({ queryKey: ["posts"] }); qc.invalidateQueries({ queryKey: ["post"] });
      nav("/admin/diario");
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} data-testid="admin-post-form">
      <PageTitle title={id ? "Modifica articolo" : "Nuovo articolo"} action={<div className="flex gap-3"><Link to="/admin/diario" className="btn-outline !py-2.5 !px-5">Annulla</Link><button type="submit" disabled={busy} data-testid="admin-post-save" className="btn-primary !py-2.5 !px-5 disabled:opacity-50">{busy ? "Salvo…" : "Salva"}</button></div>} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 space-y-5">
          <F label="Titolo *" name="title" value={f.title} onChange={set} />
          <F label="Sommario / estratto" name="excerpt" as="textarea" rows={2} value={f.excerpt} onChange={set} />
          <F label="Contenuto" name="content" as="textarea" rows={14} value={f.content} onChange={set} hint="Separa i paragrafi con una riga vuota" />
        </Card>
        <div className="space-y-6">
          <Card className="space-y-4">
            <Toggle label="Pubblicato" name="published" checked={f.published} onChange={set} />
            <F label="Categoria" name="category" as="select" value={f.category} onChange={set} options={[{ value: "Diario di bordo", label: "Diario di bordo" }, { value: "Progetti", label: "Progetti" }, { value: "News", label: "News" }]} />
            <F label="Data pubblicazione" name="published_at" type="datetime-local" value={f.published_at?.slice(0, 16)} onChange={(k, v) => set(k, new Date(v).toISOString())} />
            <F label="Slug" name="slug" value={f.slug} onChange={set} hint="Vuoto = automatico" />
          </Card>
          <Card><SingleImage label="Immagine di copertina" name="cover_image" value={f.cover_image} onChange={set} folder="blog" /></Card>
        </div>
      </div>
    </form>
  );
}
